import React, { useContext, useState } from "react";
import { Modal, Button } from "react_reusable_components";
import variables from "../../themes/base/styles/variables";
import { encrypt, getLocalizedMessage } from "../../UtilityFunctions/utils";
import { useIntl } from "react-intl";
import { ThreeRow } from "../../core/layouts";
import { useFormik } from "formik";
import {
  myAccountResetPasswordFieldNames,
  myAccountInitialValues,
  myAccountValidationSchema,
} from "../../validationSchema";
import useResponsive from "core/hooks/useResponsive";
import MyAccountService from "../../services/my-account-services";
import ResetPasswordFormModalContainer from "../ResetPasswordFormModalContainer/ResetPasswordFormModalContainer";
import ResetPasswordModalHeader from "../ResetPasswordModalHeader";
import { AuthContext } from "../../globalContext/auth/authProvider";
import { clearAuthAndLogout } from "../../globalContext/auth/authActions";
import { AuthService } from "../../services";

const MyAccountModalStyle = {
  modalContent: { width: variables.fullWidth },
  modalMaskStyle: {
    backdropFilter: variables.highBlur,
    background: variables.blurBackground,
  },
  topSectionStyle: { borderBottom: `1px solid ${variables.grey4}` },
  bottomSectionStyle: {
    borderTop: `1px solid ${variables.grey4}`,
    display: variables.dFlex,
    justifyContent: "end",
    padding: variables.paddingMedium,
    gap: variables.gapMediumSmall,
  },
  middleSectionStyle: { padding: `${variables.paddingMedium}` },
  cancelBtn: {
    border: `1px solid ${variables.grey4}`,
    backgroundColor: variables.bodyBg,
    color: variables.textDefault,
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    height: variables.buttonHeightNormal,
  },
  changeBtn: {
    fontSize: variables.fontSizeNormal,
    fontWeight: variables.fontWeightNormal,
    lineHeight: variables.lineHeightNormal,
    height: variables.buttonHeightNormal,
  },
};

export default function ResetPasswordModal({ open, onClose }) {
  const authDispatch = useContext(AuthContext)[1];
  const [disabled, setDisabled] = useState();

  const intl = useIntl();
  const {
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    errors,
    touched,
    resetForm,
  } = useFormik({
    initialValues: myAccountInitialValues,
    onSubmit: function (data) {
      setDisabled(true);
      MyAccountService.changePassword({
        body: {
          oldPassword: encrypt(
            data[myAccountResetPasswordFieldNames.OLD_PASSWORD]
          ),
          newPassword: encrypt(data[myAccountResetPasswordFieldNames.PASSWORD]),
        },
        intl,
      })
        .then(() => {
          setDisabled(false);
          onClose();
          resetForm();
          AuthService.logOut({ intl }, false)
            .then(() => {
              authDispatch(clearAuthAndLogout());
            })
            .catch(() => {
              authDispatch(clearAuthAndLogout());
            });
        })
        .catch(() => {
          setDisabled(false);
        });
    },
    validationSchema: myAccountValidationSchema(intl),
  });

  const closeModal = () => {
    onClose();
    resetForm();
  };

  const responsive = useResponsive();
  return (
    <Modal
      maskClosable={false}
      maskStyle={MyAccountModalStyle.modalMaskStyle}
      centered
      open={open}
      closable={false}
      footer={null}
      width={549}
      disableGutter
    >
      <ThreeRow
        topSection={<ResetPasswordModalHeader onClose={closeModal} />}
        topSectionStyle={MyAccountModalStyle.topSectionStyle}
        middleSectionStyle={MyAccountModalStyle.middleSectionStyle}
        middleSection={
          <ResetPasswordFormModalContainer
            errors={errors}
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            myAccountResetPasswordFieldNames={myAccountResetPasswordFieldNames}
            touched={touched}
          />
        }
        bottomSectionStyle={{
          ...MyAccountModalStyle.bottomSectionStyle,
          ...(!responsive.isMd && { justifyContent: "center" }),
        }}
        bottomSection={
          <>
            <Button
              onClick={closeModal}
              icon={null}
              style={MyAccountModalStyle.cancelBtn}
            >
              {getLocalizedMessage(intl, "button.text.cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              style={MyAccountModalStyle.changeBtn}
              disabled={disabled}
              icon={null}
            >
              {getLocalizedMessage(intl, "account.changePassword")}
            </Button>
          </>
        }
      />
    </Modal>
  );
}
